import { TextInput } from '@apeiron/temp'
import {
  Box,
  OutlinedInputProps,
  Slider as MuiSlider,
  SliderProps,
  styled
} from '@mui/material'
import {
  ChangeEvent,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import NumberFormat from 'react-number-format'

const Slider = styled(MuiSlider)`
  padding: 10px 0;

  & .MuiSlider-rail {
    color: #151615;
    opacity: 1;
  }

  & .MuiSlider-track {
    color: #e4c169;
    border: none;
  }

  & .MuiSlider-thumb {
    color: #ffffff;
    height: 12px;
    width: 12px;
  }

  & .MuiSlider-mark {
    border-radius: 100%;
    color: #c3c6c5;
    height: 6px;
    opacity: 0.5;
    width: 6px;
  }
`

const SliderContainer = styled(Box)`
  margin-left: 2px;
  padding: 0 6px;
  z-index: 10;
`

const StyledSlider = styled(Slider)`
  display: block;
  width: calc(100% - 6px);
`

const TextInputRoot = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-left: 10px;
  margin-right: 10px;
`

const NumberTextInput = styled(TextInput)`
  background-color: #565859;
  border-radius: 4px;
  border: unset;
  overflow: hidden;
  width: 75px;
  & .MuiOutlinedInput-notchedOutline {
    border: unset;
    border-radius: unset;
  }
  & .MuiInputBase-input {
    text-align: center;
  }
`

const CustomTextInput = (props: OutlinedInputProps) => {
  return (
    <NumberTextInput
      {...props}
      className='Custom-Number-Input'
      inputProps={{
        'aria-label': 'range-slider-text-input'
      }}
    />
  )
}

const RangeSliderWithInput: FC<Props> = props => {
  const {
    className,
    decimalScale = 0,
    max: sliderMax = 0,
    maxValue: sliderMaxValue = 0,
    min: sliderMin = 0,
    minValue: sliderMinValue = 0,
    label,
    descale,
    scale,
    onValueChange,
    onChangeCommitted,
    ...rest
  } = props

  const { max, maxValue, min, minValue } = useMemo(() => {
    return {
      max: scale(sliderMax),
      maxValue: scale(sliderMaxValue),
      min: scale(sliderMin),
      minValue: scale(sliderMinValue)
    }
  }, [scale, sliderMax, sliderMaxValue, sliderMin, sliderMinValue])

  const onSliderChange = useCallback(
    (_: any, values: number | number[]) => {
      if (!Array.isArray(values)) {
        return
      }

      const [minChange, maxChange] = values

      onValueChange(minChange, maxChange)
    },
    [onValueChange]
  )

  const [minText, setMinText] = useState(minValue.toString())

  const [maxText, setMaxText] = useState(maxValue.toString())

  const onMinTextChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMinText(e.target.value)
  }, [])

  const onMaxTextChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMaxText(e.target.value)
  }, [])

  const onBlur = useCallback(() => {
    let a = Number(minText.replaceAll(',', ''))

    let b = Number(maxText.replaceAll(',', ''))

    if (isNaN(a) || a > b) {
      a = minValue
    } else if (a < min) {
      a = min
    }

    if (isNaN(b) || b < a) {
      b = maxValue
    } else if (b > max) {
      b = max
    }

    const newMinValue = Math.min(a, b)

    const newMaxValue = Math.max(a, b)

    setMinText(newMinValue.toString())
    setMaxText(newMaxValue.toString())
    if (minValue !== newMinValue || maxValue !== newMaxValue) {
      onValueChange(descale(newMinValue), descale(newMaxValue))
      onChangeCommitted()
    }
  }, [
    minText,
    maxText,
    min,
    max,
    minValue,
    maxValue,
    onValueChange,
    descale,
    onChangeCommitted
  ])

  useEffect(() => {
    setMinText(minValue.toString())
    setMaxText(maxValue.toString())
  }, [minValue, maxValue])

  const sliderValue = useMemo(
    () => [sliderMinValue, sliderMaxValue],
    [sliderMinValue, sliderMaxValue]
  )

  return (
    <Box className={className}>
      {label}
      <SliderContainer>
        <StyledSlider
          min={sliderMin}
          max={sliderMax}
          value={sliderValue}
          scale={scale}
          onChange={onSliderChange}
          onChangeCommitted={onChangeCommitted}
          getAriaLabel={index => `range slider index ${index}`}
          {...rest}
        />
      </SliderContainer>
      <TextInputRoot>
        <NumberFormat<OutlinedInputProps>
          customInput={CustomTextInput}
          decimalScale={decimalScale}
          thousandSeparator=','
          value={minText}
          onBlur={onBlur}
          onChange={onMinTextChange}
        />
        <NumberFormat<OutlinedInputProps>
          customInput={CustomTextInput}
          decimalScale={decimalScale}
          thousandSeparator=','
          value={maxText}
          onBlur={onBlur}
          onChange={onMaxTextChange}
        />
      </TextInputRoot>
    </Box>
  )
}

type Props = Omit<SliderProps, 'onChangeCommitted'> & {
  className?: string
  decimalScale?: number
  label?: ReactNode
  maxValue: number
  minValue: number
  descale: (value: number) => number
  scale: (value: number) => number
  onValueChange: (sliderNewMin: number, sliderNewMax: number) => void
  onChangeCommitted: () => void
}

export default RangeSliderWithInput
